import { Badge } from '../ui/badge';
import { FileWarning, CheckCircle2, XCircle } from 'lucide-react';

export type ConcessionPOStatus = 'under_review' | 'approved' | 'rejected' | null | undefined;

interface ConcessionStatusBadgeProps {
  status: ConcessionPOStatus;
  /** Compact form for table cells — icon plus a short word. */
  compact?: boolean;
}

const CONFIG = {
  under_review: {
    label: 'Concession under review',
    shortLabel: 'Under review',
    className: 'bg-amber-100 text-amber-800 border-amber-300',
    Icon: FileWarning,
    title: 'A deviation has been raised against this line and is awaiting a decision.',
  },
  approved: {
    label: 'Concession approved',
    shortLabel: 'Approved',
    className: 'bg-green-100 text-green-800 border-green-300',
    Icon: CheckCircle2,
    title: 'A deviation against this line was accepted — its parts were approved out of specification.',
  },
  rejected: {
    label: 'Concession rejected',
    shortLabel: 'Rejected',
    className: 'bg-red-100 text-red-800 border-red-300',
    Icon: XCircle,
    title: 'A deviation against this line was rejected.',
  },
} as const;

/**
 * CR-6510: the concession state of a PO line, for procurement.
 *
 * A buyer reading an order line could not tell that its parts had been
 * accepted out of spec, or that a decision was outstanding, without going
 * to look in Quality. Renders nothing when no concession has been raised,
 * so a clean line stays visually clean.
 */
export function ConcessionStatusBadge({ status, compact = false }: ConcessionStatusBadgeProps) {
  if (!status) return null;
  const config = CONFIG[status];
  if (!config) return null;
  const { Icon } = config;

  return (
    <Badge variant="outline" className={config.className} title={config.title}>
      <Icon className="h-3 w-3 mr-1" />
      {compact ? config.shortLabel : config.label}
    </Badge>
  );
}
